import { useCallback, useEffect, useState } from 'react';
import axiosInstance from '../utils/axios';
import { APIS } from '../utils/serviceUrls';

// Phase 6.8: data layer for the customer-care impersonation API.
//
// The hook exposes:
//   - start(email, reason)       — support agent kicks off a session
//   - endCurrent(token)          — end an impersonation via /current
//   - refreshHistory()           — agent's own history
//   - refreshActive()            — super-admin: every active session
//   - revokeByJti(jti)           — super-admin: force-revoke a session

export interface ImpersonationLog {
  id: string;
  sessionJti: string;
  agentId: string;
  targetUserId: string;
  reason: string;
  ipAddress: string;
  deviceType: string;
  browser: string;
  os: string;
  startedAt: string;
  expiresAt: string;
  endedAt?: string | null;
  endedReason?: string | null;
  endedBy?: string | null;
}

export interface StartImpersonationResponse {
  accessToken: string;
  jti: string;
  expiresAt: string;
  ttlSeconds: number;
  target: { _id: string; email: string; name?: string };
  agent: { _id: string; email: string };
  temporaryAccess: {
    email: string;
    accessCode: string;
    loginUrl: string;
    expiresAt: string;
  };
}

export default function useImpersonation() {
  const [history, setHistory] = useState<ImpersonationLog[]>([]);
  const [active, setActive] = useState<ImpersonationLog[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refreshHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(APIS.IMPERSONATION.HISTORY);
      const data = res.data?.data ?? res.data;
      setHistory(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load impersonation history');
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshActive = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(APIS.IMPERSONATION.ACTIVE);
      const data = res.data?.data ?? res.data;
      setActive(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load active impersonations');
      setActive([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const start = useCallback(
    async (
      targetEmail: string,
      reason: string,
    ): Promise<StartImpersonationResponse> => {
      const res = await axiosInstance.post(APIS.IMPERSONATION.START, {
        targetEmail,
        reason,
      });
      const data = res.data?.data ?? res.data;
      return data as StartImpersonationResponse;
    },
    [],
  );

  // End-current uses the impersonation token itself (NOT the agent's
  // session token) so the backend can read req.user.actor and the jti.
  // We attach the token explicitly to bypass axiosInstance's interceptor.
  const endCurrent = useCallback(async (token: string): Promise<void> => {
    await axiosInstance.delete(APIS.IMPERSONATION.END_CURRENT, {
      headers: { Authorization: `Bearer ${token}` },
      // Skip the auth-token-injection interceptor for this call.
      transformRequest: (data, headers) => {
        if (headers) {
          headers.Authorization = `Bearer ${token}`;
        }
        return data;
      },
    });
  }, []);

  const revokeByJti = useCallback(async (jti: string): Promise<void> => {
    await axiosInstance.delete(APIS.IMPERSONATION.BY_JTI(jti));
  }, []);

  const historyByTarget = useCallback(
    async (email: string): Promise<ImpersonationLog[]> => {
      const res = await axiosInstance.get(
        APIS.IMPERSONATION.HISTORY_BY_TARGET(email),
      );
      const data = res.data?.data ?? res.data;
      return Array.isArray(data) ? data : [];
    },
    [],
  );

  return {
    history,
    active,
    loading,
    error,
    refreshHistory,
    refreshActive,
    start,
    endCurrent,
    revokeByJti,
    historyByTarget,
  };
}
